import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import UsersManagementService from "../../services/userManagementApi/usersManagement.service";
import { CellFormulaValue, CellHyperlinkValue, Workbook } from "exceljs";
import moment from "moment";
import { IAbsenceMassiveIn } from "../../models/input/generalApi/IAbsenceMassiveIn";

export const useAbsencesMassive = () => {
  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [openModalCreateDone, setOpenModalCreateDone] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [buttonSaveDisabled, setButtonSaveDisabled] = useState(true);
  const [jsonAbsences, setJsonAbsences] = useState<IAbsenceMassiveIn[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [messageError, setErrorMessage] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);

  const handleDownloadTemplate = () => {
    setLoading(true)
    UsersManagementService.getTempateAbsencesMassive(Number(languageID.value))
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            setOpenModalError(true);
            console.error('Error:', "Download failed");
          } else if (r instanceof Blob) {
            try {
              let fileName = 'template_create_absences_massive';
              const url = window.URL.createObjectURL(r);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', fileName + '.xlsx');
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
            } catch (error) {
              setOpenModalError(true);
              console.error('Error:', error);
            }
          }
        } catch (error) {
          setOpenModalError(true);
          console.error('Error:', error);
        } finally {
          setLoading(false)
        }
      })
  }

  const handleCreateAbsences = () => {
    setOpenModalCreate(true);
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const handleOnClickExit = () => {
    setFileName("");
    setOpenModalCreate(false);
    setButtonSaveDisabled(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    if (!fileInput.files) {
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      return;
    }
    
    const file = fileInput.files[0];
    setFileName(file.name);
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();
    
    if (fileType?.toLowerCase() !== "xlsx") {
      setErrorMessage(t("header.messageXLSX"));
      setErrorFilePicker(true);
      return;
    }
    
    setLoadingModal(true);
    setErrorFilePicker(false);
    reader.onload = async (event) => {
      const data = event.target?.result as ArrayBuffer;
      const workbook = new Workbook();

      try {
        await workbook.xlsx.load(data);
        const hiddenSheet = workbook.getWorksheet("Data");
        var advisorsMap: Record<string, number> = {};
        var absencesTypeMap: Record<string, number> = {};
        const rows: IAbsenceMassiveIn[] = [];

        if (!hiddenSheet) {
          throw new Error("hidden sheet not found");
        }

        // set map of data list
        // advisors list
        let row = 1;
        while (hiddenSheet.getCell(`A${row}`).value && hiddenSheet.getCell(`B${row}`).value) {
          const propertyID = Number(hiddenSheet.getCell(`A${row}`).value);
          const propertyName = hiddenSheet.getCell(`B${row}`).value?.toString().trim();
          
          if (propertyName?.trim() && !advisorsMap[propertyName?.trim()]) {
            advisorsMap[propertyName?.trim()] = propertyID;
          }
          row++;
        }

        // absences type list
        row = 1;
        while (hiddenSheet.getCell(`C${row}`).value && hiddenSheet.getCell(`D${row}`).value) {
          const propertyID = Number(hiddenSheet.getCell(`C${row}`).value);
          const propertyName = hiddenSheet.getCell(`D${row}`).value?.toString().trim();
          
          if (propertyName?.trim() && !absencesTypeMap[propertyName?.trim()]) {
            absencesTypeMap[propertyName?.trim()] = propertyID;
          }
          row++;
        }

        const sheet = workbook.worksheets[0];
        var error = false;
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1 || error) {
            return;
          }

          if (!getValueCellTemplate(row.getCell(1).value)) {
            return;
          }

          let userID, name, absenceType, startDate, endDate, comments;
          userID = getValueCellTemplate(row.getCell(1).value);
          name = getValueCellTemplate(row.getCell(2).value);
          absenceType = getValueCellTemplate(row.getCell(3).value);
          startDate = getValueDatesCellTemplate(row.getCell(4).value);
          endDate = getValueDatesCellTemplate(row.getCell(5).value);
          comments = getValueCellTemplate(row.getCell(6).value);

          if (!(startDate && moment(startDate, "YYYY-MM-DD", true).isValid() &&
            endDate && moment(endDate, "YYYY-MM-DD", true).isValid() && moment(startDate).isSameOrBefore(endDate) &&
            moment(startDate).isSameOrAfter(moment().subtract(2, 'days'), 'day'))
          ) {
              error = true;
              setButtonSaveDisabled(true);
              setErrorFilePicker(true);
              setErrorMessage(t("modal.incorrectDatesAbsences").replace('{0}', rowNumber.toString()));
              return;
          }

          if (userID && name && name !== "#N/A" && absenceType && absencesTypeMap[absenceType] && 
            startDate && moment(startDate, "YYYY-MM-DD", true).isValid() &&
            endDate && moment(endDate, "YYYY-MM-DD", true).isValid()) {

            const rowData: IAbsenceMassiveIn = {
              advisorID: advisorsMap[name],
              nameAdvisor: name,
              startDate: moment(startDate).format("YYYY-MM-DD"),
              endDate: moment(endDate).format("YYYY-MM-DD"),
              absenceTypeID: absencesTypeMap[absenceType],
              absenceType: absenceType,
              descriptionText: comments || ''
            }

            rows.push(rowData);
          } else {
            error = true;
            setButtonSaveDisabled(true);
            setErrorFilePicker(true);
            setErrorMessage(t("modal.invalidDataUsersMassive").replace('{0}', rowNumber.toString()));
          }
        });

        setJsonAbsences(rows);
      } catch (error) {
        console.error(error);
        setErrorFilePicker(true);
        setButtonSaveDisabled(true);
        setErrorMessage(t("modal.errorLoadingFile"));
      }

      setLoadingModal(false);
    };

    reader.readAsArrayBuffer(file);
    setButtonSaveDisabled(false);
  };

  const onClearFilePicker = () => {
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const submitAbsences = async () => {

    setLoadingModal(true);
    interface DataItem {
      advisorID: number;
      nameAdvisor: string;
      absenceType: string;
      startDate: string;
      endDate: string;
      result: number;
      message: string;
    }

    try {
      const response: any[] = await UsersManagementService.createAbsencesInBulk(jsonAbsences);
      
      if (!response) {
        setOpenModalCreate(false);
        setOpenModalError(true);
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Datos");

      worksheet.columns = [
        { header: t("header.userId"), key: "advisorID", width: 15 },
        { header: t("header.name"), key: "nameAdvisor", width: 15 },
        { header: t("header.type"), key: "absenceType", width: 15 },
        { header: t("header.stardDate"), key: "startDate", width: 15 },
        { header: t("header.endDate"), key: "endDate", width: 15 },
        { header: t("modal.message"), key: "message", width: 30 },
      ];

      response.forEach((item: DataItem) => {

        item.message = item.result === 1 ? t("modal.absenceCreated")
        : item.result === 2 ? t("modal.absenceDuplicated")
        : item.result === 3 ? t("modal.absenceInCheckInChecOutCompleted")
        : t("modal.generalError"); 

        const row = worksheet.addRow(item);
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "00B0F0" } };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
        cell.font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      setOpenModalCreate(false);
      setOpenModalCreateDone(true);

      const a = document.createElement("a");
      a.href = url;
      a.download = "absences_result.xlsx";
      a.click();

      URL.revokeObjectURL(url);
      setLoadingModal(false);
    } catch (error) {
      setOpenModalCreate(false);
      setOpenModalError(true);
      setLoadingModal(false);
    }
  };

  const getValueCellTemplate = (cellValue: any): string | undefined => {

    if (typeof cellValue === 'string' || typeof cellValue === 'number') {
      return cellValue?.toString().trim();
    }

    if ((cellValue as CellHyperlinkValue)?.text) {
      return (cellValue as CellHyperlinkValue)?.text?.toString().trim();
    }

    if ((cellValue as CellFormulaValue)?.result) {
      return (cellValue as CellFormulaValue)?.result?.toString().trim();
    }

    return;
  }

  const getValueDatesCellTemplate = (cellValue: any) => {

    var date: string | undefined;
    if (typeof cellValue === "string") {
      date = moment(cellValue, "YYYY/MM/DD", true).utc().format("YYYY-MM-DD");
    } else if (typeof cellValue === "number") {
      date = moment.utc(new Date((cellValue - 25569) * 86400 * 1000)).format("YYYY-MM-DD");
    } else if (typeof cellValue === "object" && cellValue instanceof Date) {
      date = moment(cellValue).utc().format("YYYY-MM-DD");
    }

    return date;
  }
  
  return {
    handleCreateAbsences,
    setOpenModalHelp,
    openModalHelp,
    loading,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    buttonSaveDisabled,
    handleFileChange,
    fileName,
    errorFilePicker,
    messageError,
    submitAbsences,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError
  };
}